import {
  type RestoreAccount,
  type Account,
  type SourceAccount,
  Provider,
  CloudAccountType,
} from '@repo/api-gw-sdk';

export const isSourceAccount = (
  account?: Account | SourceAccount | RestoreAccount
): account is SourceAccount =>
  !!account && 'sourceAccountAttributes' in account;

export const isRestoreAccount = (
  account?: Account | SourceAccount | RestoreAccount
): account is RestoreAccount =>
  !!account && 'restoreAccountAttributes' in account;

export const getAccountType = (
  account?: Account | SourceAccount | RestoreAccount
): CloudAccountType => {
  if (!account) {
    return CloudAccountType.AccountTypeUnspecified;
  }
  return isSourceAccount(account)
    ? CloudAccountType.Source
    : isRestoreAccount(account)
      ? CloudAccountType.Restore
      : account.accountType;
};

export const getAccountConnectedTime = (
  account: Account | SourceAccount | RestoreAccount
): Date | undefined => {
  if (!account) {
    return undefined;
  }
  return isSourceAccount(account) || isRestoreAccount(account)
    ? account.connectedTime
    : account.createdTime;
};

export const getAccountState = (
  account: Account | SourceAccount | RestoreAccount
) =>
  isSourceAccount(account) || isRestoreAccount(account)
    ? account.status
    : account.state;

export const getAccountRoleArn = (
  account: Account | SourceAccount | RestoreAccount
) => {
  if (isSourceAccount(account)) {
    return account.sourceAccountAttributes.aws?.roleArn;
  }
  if (isRestoreAccount(account)) {
    return account.restoreAccountAttributes.aws?.roleArn;
  }

  return account.role;
};

export const getAccountServiceAccount = (
  account: Account | SourceAccount | RestoreAccount
) => {
  if (isSourceAccount(account)) {
    return account.sourceAccountAttributes.gcp?.serviceAccount;
  }
  if (isRestoreAccount(account)) {
    return account.restoreAccountAttributes.gcp?.serviceAccount;
  }

  return account.role;
};

export const getAccountProvider = (
  account?: Account | SourceAccount | RestoreAccount
): Provider => {
  if (!account) {
    return Provider.ProviderUnspecified;
  }
  return isSourceAccount(account)
    ? account.sourceAccountAttributes.cloudProvider
    : isRestoreAccount(account)
      ? account.restoreAccountAttributes.cloudProvider
      : account.cloudProvider;
};

export const getCloudAccountNoun = (provider: Provider): string => {
  switch (provider) {
    case Provider.Azure:
      return 'subscription';
    case Provider.Gcp:
    case Provider.MongoAtlas:
      return 'project';
    default:
      return 'account';
  }
};
